import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Save, RotateCcw, Crown, Award, Sparkles,
  Wine, Shield, UtensilsCrossed, Check, AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import {
  packages as defaultPackages,
  drinkItems as defaultDrinkItems,
  STRUCTURE_FEE_PER_PERSON,
  ALMOCO_JANTAR,
  eventTypes,
} from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBRL = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PricingPage = () => {
  const navigate = useNavigate();
  const [overrides, setOverrides] = useState({ packages: {}, drinks: {}, structureFee: null, almocoJantar: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [activeGroup, setActiveGroup] = useState('all'); // group filter

  // Load overrides
  useEffect(() => {
    axios.get(`${API}/pricing`)
      .then((res) => {
        setOverrides({
          packages: res.data.packages || {},
          drinks: res.data.drinks || {},
          structureFee: res.data.structureFee,
          almocoJantar: res.data.almocoJantar,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Helpers
  const currentPkgPrice = (pkg) => {
    const o = overrides.packages[pkg.id];
    return (o && typeof o.pricePerPerson === 'number') ? o.pricePerPerson : pkg.pricePerPerson;
  };
  const currentDrinkPrice = (d) => {
    const o = overrides.drinks[d.id];
    return (o && typeof o.pricePerPerson === 'number') ? o.pricePerPerson : d.pricePerPerson;
  };
  const currentStructure = overrides.structureFee ?? STRUCTURE_FEE_PER_PERSON;
  const currentAlmoco = overrides.almocoJantar ?? ALMOCO_JANTAR.pricePerPerson;

  const setPkgPrice = (id, val) => {
    setOverrides((p) => ({ ...p, packages: { ...p.packages, [id]: { pricePerPerson: val } } }));
  };
  const setDrinkPrice = (id, val) => {
    setOverrides((p) => ({ ...p, drinks: { ...p.drinks, [id]: { pricePerPerson: val } } }));
  };
  const resetPkg = (id) => {
    setOverrides((p) => {
      const np = { ...p.packages };
      delete np[id];
      return { ...p, packages: np };
    });
  };
  const resetDrink = (id) => {
    setOverrides((p) => {
      const nd = { ...p.drinks };
      delete nd[id];
      return { ...p, drinks: nd };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/pricing`, overrides);
      setSavedAt(res.data.updated_at);
      setTimeout(() => setSavedAt(null), 4000);
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const resetAll = () => {
    if (!window.confirm('Voltar todos os preços para os valores originais?')) return;
    setOverrides({ packages: {}, drinks: {}, structureFee: null, almocoJantar: null });
  };

  // Group packages by event group
  const groupedPackages = useMemo(() => {
    const map = {};
    defaultPackages.forEach((p) => {
      if (!map[p.group]) map[p.group] = [];
      map[p.group].push(p);
    });
    return map;
  }, []);

  const groupLabels = useMemo(() => {
    const m = {};
    eventTypes.forEach((e) => { m[e.packageGroup] = e.label; });
    m['casamento'] = 'Casamento';
    m['aniversario'] = 'Aniversário';
    m['debutante'] = '15 Anos / Debutante';
    m['infantil'] = 'Festa Infantil';
    m['boteco'] = 'Boteco / Corporativo';
    m['churrasco'] = 'Churrasco';
    return m;
  }, []);

  const allGroups = useMemo(() => Object.keys(groupedPackages), [groupedPackages]);
  const visibleGroups = activeGroup === 'all' ? allGroups : [activeGroup];

  const totalChanged = Object.keys(overrides.packages).length
    + Object.keys(overrides.drinks).length
    + (overrides.structureFee !== null && overrides.structureFee !== undefined ? 1 : 0)
    + (overrides.almocoJantar !== null && overrides.almocoJantar !== undefined ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] font-body">
      {/* Header */}
      <header className="bg-[#111111] border-b border-white/5 px-4 sm:px-6 py-4 sticky top-0 z-20 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/admin')} data-testid="back-to-dashboard" className="text-white/40 hover:text-white transition-colors flex-shrink-0">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="font-display text-lg sm:text-xl text-white truncate">Preços <span className="text-[#C9A961]">do sistema</span></h1>
              <p className="font-body text-[11px] text-white/35">Alterações aplicam-se imediatamente no site.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {totalChanged > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 text-[10px] uppercase tracking-wider text-[#E6CC8A]">
                <AlertCircle className="w-3 h-3" />
                {totalChanged} alteração{totalChanged > 1 ? 'ões' : ''}
              </span>
            )}
            {savedAt && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[10px] uppercase tracking-wider text-emerald-400">
                <Check className="w-3 h-3" /> Salvo
              </span>
            )}
            <button onClick={resetAll} data-testid="reset-all-prices" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/10 hover:border-white/20 text-white/50 hover:text-white font-body text-xs uppercase tracking-wider transition-all">
              <RotateCcw className="w-3.5 h-3.5" /> Resetar
            </button>
            <button onClick={save} disabled={saving} data-testid="save-pricing" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-xs sm:text-sm font-semibold tracking-wider uppercase px-4 sm:px-5 py-2.5 rounded-md hover:shadow-lg hover:shadow-[#C9A961]/25 active:scale-[0.97] transition-all disabled:opacity-50">
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="text-center py-20 text-white/40">Carregando...</div>
        ) : (
          <>
            {/* GLOBAL ITEMS (structure + almoço) */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-[#C9A961]" />
                <h2 className="font-display text-lg text-white">Itens globais</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PriceCard
                  testid="price-structure"
                  icon={Shield}
                  title="Estrutura Buffet"
                  subtitle="Por pessoa — adicionado automaticamente"
                  value={currentStructure}
                  defaultValue={STRUCTURE_FEE_PER_PERSON}
                  changed={overrides.structureFee !== null && overrides.structureFee !== undefined}
                  onChange={(v) => setOverrides((p) => ({ ...p, structureFee: v }))}
                  onReset={() => setOverrides((p) => ({ ...p, structureFee: null }))}
                />
                <PriceCard
                  testid="price-almoco-jantar"
                  icon={UtensilsCrossed}
                  title="Almoço/Jantar (substituto)"
                  subtitle="Adicional sobre o pacote escolhido"
                  value={currentAlmoco}
                  defaultValue={ALMOCO_JANTAR.pricePerPerson}
                  changed={overrides.almocoJantar !== null && overrides.almocoJantar !== undefined}
                  onChange={(v) => setOverrides((p) => ({ ...p, almocoJantar: v }))}
                  onReset={() => setOverrides((p) => ({ ...p, almocoJantar: null }))}
                />
              </div>
            </section>

            {/* DRINKS */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Wine className="w-4 h-4 text-[#C9A961]" />
                <h2 className="font-display text-lg text-white">Bebidas</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {defaultDrinkItems.map((d) => {
                  const cur = currentDrinkPrice(d);
                  const changed = !!overrides.drinks[d.id];
                  return (
                    <PriceCard
                      key={d.id}
                      testid={`price-drink-${d.id}`}
                      title={d.label}
                      subtitle={d.description}
                      value={cur}
                      defaultValue={d.pricePerPerson}
                      changed={changed}
                      onChange={(v) => setDrinkPrice(d.id, v)}
                      onReset={() => resetDrink(d.id)}
                    />
                  );
                })}
              </div>
            </section>

            {/* PACKAGES — group filter */}
            <section>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9A961]" />
                  <h2 className="font-display text-lg text-white">Pacotes</h2>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <FilterChip active={activeGroup === 'all'} onClick={() => setActiveGroup('all')}>Todos</FilterChip>
                  {allGroups.map((g) => (
                    <FilterChip key={g} active={activeGroup === g} onClick={() => setActiveGroup(g)}>
                      {groupLabels[g] || g}
                    </FilterChip>
                  ))}
                </div>
              </div>

              {visibleGroups.map((group) => (
                <div key={group} className="mb-6">
                  <h3 className="font-body text-[11px] uppercase tracking-[0.18em] text-[#C9A961]/85 mb-3">
                    {groupLabels[group] || group}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(groupedPackages[group] || []).map((pkg) => {
                      const cur = currentPkgPrice(pkg);
                      const changed = !!overrides.packages[pkg.id];
                      const TierIcon = pkg.tier === 'premium' ? Crown : (pkg.tier === 'especial' ? Award : Sparkles);
                      return (
                        <PriceCard
                          key={pkg.id}
                          testid={`price-pkg-${pkg.id}`}
                          icon={TierIcon}
                          tier={pkg.tier}
                          title={pkg.name}
                          subtitle={pkg.summary}
                          value={cur}
                          defaultValue={pkg.pricePerPerson}
                          changed={changed}
                          onChange={(v) => setPkgPrice(pkg.id, v)}
                          onReset={() => resetPkg(pkg.id)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>

            {/* Floating mobile save button */}
            <div className="sm:hidden h-20" />
            <div className="sm:hidden fixed bottom-0 left-0 right-0 px-4 py-3 bg-[#0a0a0a]/95 backdrop-blur-md border-t border-white/10 flex gap-2 z-30">
              <button onClick={resetAll} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-md border border-white/10 text-white/55 font-body text-xs uppercase tracking-wider">
                <RotateCcw className="w-3.5 h-3.5" /> Resetar
              </button>
              <button onClick={save} disabled={saving} className="flex-[2] inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-[#1a1611] font-body text-xs font-semibold tracking-wider uppercase px-4 py-3 rounded-md active:scale-[0.97] transition-all disabled:opacity-50">
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition-all ${
      active
        ? 'bg-[#C9A961] text-[#1a1611] font-semibold'
        : 'border border-white/10 text-white/50 hover:text-white hover:border-white/25'
    }`}
  >
    {children}
  </button>
);

const PriceCard = ({ icon: Icon, tier, title, subtitle, value, defaultValue, changed, onChange, onReset, testid }) => {
  const [local, setLocal] = useState(String(value));
  useEffect(() => { setLocal(String(value)); }, [value]);

  const commit = () => {
    const v = parseFloat(local.replace(',', '.'));
    if (Number.isNaN(v) || v < 0) {
      setLocal(String(value));
      return;
    }
    onChange(v);
  };

  const tierBadge = tier === 'premium' ? { label: 'Premium', color: 'text-[#E6CC8A] border-[#C9A961]/40 bg-[#C9A961]/10' }
                  : tier === 'especial' ? { label: 'Especial', color: 'text-[#E6CC8A] border-[#C9A961]/40 bg-[#C9A961]/10' }
                  : tier === 'tradicional' ? { label: 'Tradicional', color: 'text-white/55 border-white/15 bg-white/[0.04]' }
                  : null;

  return (
    <div data-testid={testid} className={`p-4 rounded-lg border transition-all ${changed ? 'border-[#C9A961]/50 bg-gradient-to-br from-[#C9A961]/[0.04] to-transparent' : 'border-white/[0.08] bg-white/[0.012]'}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {Icon && <Icon className="w-4 h-4 text-[#C9A961] mt-0.5 flex-shrink-0" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-body text-[13.5px] sm:text-sm text-white font-medium leading-tight">{title}</span>
              {tierBadge && (
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${tierBadge.color}`}>
                  {tierBadge.label}
                </span>
              )}
            </div>
            {subtitle && <span className="font-body text-[11px] text-white/40 block leading-snug mt-0.5">{subtitle}</span>}
          </div>
        </div>
        {changed && (
          <button onClick={onReset} title="Voltar ao padrão" className="text-white/30 hover:text-[#C9A961] transition-colors flex-shrink-0">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex items-end gap-2 mt-3">
        <span className="font-body text-xs text-white/40 mb-2">R$</span>
        <input
          type="text"
          inputMode="decimal"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="flex-1 bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none text-white font-display text-2xl font-bold tabular-nums pb-1 transition-colors"
        />
        <span className="font-body text-[10px] text-white/35 mb-2 uppercase tracking-wider">/p</span>
      </div>
      {changed && (
        <p className="text-[10px] text-white/35 mt-1">Padrão: R$ {fmtBRL(defaultValue)}</p>
      )}
    </div>
  );
};

export default PricingPage;
